import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from '../Link';
import { PRIMARY_NAV_LINKS, primaryNavLabel } from './primary-nav-links';
import { MyJobsNavEntry } from './MyJobsNavEntry';
import { Logo } from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { UserMenu } from './UserMenu';
import type { IntlShape } from 'react-intl';
import { useJobIslandContext } from '../../contexts/JobIslandContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { trackEvent } from '../../utils/analytics/analytics';
import { CORE_HEADER_STICKY, CORE_NAV_LINK } from '../../constants/corePublicTheme';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

export interface NavigationNavbarProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
}

/**
 * Navigation navbar component - shows on non-SRP pages
 * Contains logo, navigation links, tools menu, language selector, and user menu
 */
export const NavigationNavbar: React.FC<NavigationNavbarProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  clearSearch,
  user,
}) => {
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const pathname = jobIsland?.pathname ?? (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  // Prefer job island, then pageContext, then intl, then pathname so static and hydrated both show correct locale
  const rawLocale = jobIsland?.locale ?? pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en';
  const locale = normalizeSupportedLocale(rawLocale);

  const auth = useAuthOptional();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const showGuestSignIn = hasMounted && auth && !auth.isAuthenticated && typeof auth.signIn === 'function';

  return (
    <header className={CORE_HEADER_STICKY} role="banner">
      <nav className="flex items-center gap-2 py-3 px-4 xl:px-16 md:px-10" aria-label="Main navigation">
        {/* Left side: Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Logo clearSearch={clearSearch} variant="horizontal" locale={locale} />
          <Logo clearSearch={clearSearch} variant="compact" locale={locale} />
        </div>

        {/* Center: same primary nav on every page — link set comes from PRIMARY_NAV_LINKS,
            shared with the static ResourceNavbar twin so the two navbars cannot drift. */}
        <nav className="hidden lg:flex flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* mx-auto (not justify-center on the scroll container) so links stay centered when
              they fit, but when a long locale's labels overflow, scrolling from the start
              reaches every link instead of clipping the first one off unreachably. */}
          <div className="flex items-center gap-2 mx-auto">
            {PRIMARY_NAV_LINKS.map((link) => (
              <Link
                key={link.key}
                href={link.logicalPath}
                locale={locale}
                className={CORE_NAV_LINK}
              >
                {primaryNavLabel(link, locale)}
              </Link>
            ))}
          </div>
        </nav>

        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {showGuestSignIn ? (
            <div className="hidden md:flex items-center gap-2 mr-1">
              <button
                type="button"
                onClick={() => {
                  trackEvent({ name: 'auth_interaction', params: { action: 'sign_in_click', surface: 'navbar', locale } });
                  auth.signIn();
                }}
                className="text-sm font-semibold text-[#3B5E45] hover:text-[#2A4533] dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] px-3 py-1.5 rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/90 dark:bg-[#1F1509]/90 hover:bg-[#F2EADF] dark:hover:bg-[#342415] cursor-pointer"
              >
                <Msg
                  id="engagement.nav.signIn"
                  defaultMessage="Sign in"
                  description="Desktop nav sign-in button for guests"
                />
              </button>
            </div>
          ) : null}
          <div className="hidden sm:block">
            <MyJobsNavEntry locale={locale} />
          </div>
          <LanguageSelector intl={intl} formatMessage={formatMessage} locale={locale} />
          <UserMenu
            intl={intl}
            formatMessage={formatMessage}
            user={user}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        </div>
      </nav>
    </header>
  );
};
